import React, { useEffect, useState, useRef } from 'react';
import { profileAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate, getInitials } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Avatar, AvatarImage, AvatarFallback } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  User, Mail, Phone, Calendar, Camera, Loader2, Save, Trash2, 
  Shield, Building2, Home, Crown, CheckCircle, Lock, Eye, EyeOff, KeyRound,
  Briefcase, GraduationCap, MapPin, AlertCircle, Heart, Cake, UserX
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    // Tenant-specific fields
    birthday: '',
    school_name: '',
    student_id: '',
    home_address: '',
    emergency_contact: {
      name: '',
      relationship: '',
      phone: '',
      address: ''
    }
  });
  const fileInputRef = useRef(null);
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await profileAPI.getProfile();
      setProfile(response.data);
      setFormData({
        name: response.data.name || '',
        phone: response.data.phone || '',
        birthday: response.data.birthday || '',
        occupation: response.data.occupation || '',
        is_student: response.data.is_student || false,
        student_id: response.data.student_id || '',
        home_address: response.data.home_address || '',
        emergency_contact: {
          name: response.data.emergency_contact?.name || '',
          relationship: response.data.emergency_contact?.relationship || '',
          phone: response.data.emergency_contact?.phone || '',
          address: response.data.emergency_contact?.address || ''
        }
      });
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  // Calculate age from birthday
  const calculateAge = (birthday) => {
    if (!birthday) return null;
    const birthDate = new Date(birthday);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      const data = new FormData();
      data.append('name', formData.name);
      if (formData.phone) {
        data.append('phone', formData.phone);
      }
      
      // Tenant-specific fields
      if (profile?.role === 'tenant') {
        data.append('birthday', formData.birthday || '');
        data.append('school_name', formData.school_name || '');
        data.append('student_id', formData.student_id || '');
        data.append('home_address', formData.home_address || '');
        data.append('emergency_contact_name', formData.emergency_contact.name || '');
        data.append('emergency_contact_relationship', formData.emergency_contact.relationship || '');
        data.append('emergency_contact_phone', formData.emergency_contact.phone || '');
        data.append('emergency_contact_address', formData.emergency_contact.address || '');
      }

      const response = await profileAPI.updateProfile(data);
      setProfile(response.data);
      
      // Update auth context
      if (updateUser) {
        updateUser({ ...user, name: formData.name, phone: formData.phone });
      }
      
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, GIF, or WEBP image');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingAvatar(true);
    try {
      const data = new FormData();
      data.append('file', file);  // Backend expects 'file' field

      const response = await profileAPI.uploadAvatar(data);
      const newAvatarUrl = response.data.avatar_url;
      setProfile(prev => ({ ...prev, picture: newAvatarUrl, avatar: newAvatarUrl }));
      
      // Update auth context
      if (updateUser) {
        updateUser({ ...user, picture: newAvatarUrl, avatar: newAvatarUrl });
      }
      
      toast.success('Avatar uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!window.confirm('Are you sure you want to remove your avatar?')) return;

    setUploadingAvatar(true);
    try {
      await profileAPI.deleteAvatar();
      setProfile(prev => ({ ...prev, picture: null }));
      
      // Update auth context
      if (updateUser) {
        updateUser({ ...user, picture: null });
      }
      
      toast.success('Avatar removed');
    } catch (error) {
      toast.error('Failed to remove avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleChangePassword = async () => {
    // Validate
    if (!passwordData.currentPassword) {
      toast.error('Please enter your current password');
      return;
    }
    if (!passwordData.newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordData.currentPassword === passwordData.newPassword) {
      toast.error('New password must be different from current password');
      return;
    }

    setChangingPassword(true);
    try {
      await profileAPI.changePassword(passwordData.currentPassword, passwordData.newPassword);
      toast.success('Password changed successfully! A confirmation email has been sent.');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return Crown;
      case 'landlord': return Building2;
      case 'tenant': return Home;
      default: return User;
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setDeletingAccount(true);
    try {
      await profileAPI.deleteAccount();
      toast.success('Your account has been deleted.');
      // Log out and redirect
      localStorage.clear();
      window.location.href = '/login';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeletingAccount(false);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-purple-500';
      case 'landlord': return 'bg-[#2F4F4F]';
      case 'tenant': return 'bg-[#6B8E6B]';
      default: return 'bg-gray-500';
    }
  };

  const getAvatarUrl = (picture) => {
    if (!picture) return null;
    if (picture.startsWith('http')) return picture;
    if (picture.startsWith('/api')) return `${API_BASE}${picture}`;
    // S3 keys like "avatars/abc.jpg" — route through S3 proxy
    return `${API_BASE}/api/files/s3/${picture}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const RoleIcon = getRoleIcon(profile?.role);

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="profile-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl lg:text-3xl font-bold">My Profile</h1>
        <p className="text-muted-foreground mt-1">Manage your account settings and preferences</p>
      </div>

      {/* Avatar Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-[#C85A42]" />
            Profile Photo
          </CardTitle>
          <CardDescription>Upload a photo to personalize your account</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-center gap-6">
            {/* Avatar Preview */}
            <div className="relative group">
              <Avatar className="w-32 h-32 border-4 border-white shadow-lg">
                <AvatarImage src={getAvatarUrl(profile?.picture)} />
                <AvatarFallback className={`${getRoleColor(profile?.role)} text-white text-3xl font-bold`}>
                  {getInitials(profile?.name)}
                </AvatarFallback>
              </Avatar>
              
              {/* Upload Overlay */}
              <button
                onClick={handleAvatarClick}
                disabled={uploadingAvatar}
                className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              >
                {uploadingAvatar ? (
                  <Loader2 className="w-8 h-8 text-white animate-spin" />
                ) : (
                  <Camera className="w-8 h-8 text-white" />
                )}
              </button>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleAvatarChange}
                className="hidden"
                data-testid="avatar-input"
              />
            </div>

            {/* Avatar Actions */}
            <div className="flex-1 text-center sm:text-left space-y-3">
              <div>
                <h3 className="font-semibold text-lg">{profile?.name}</h3>
                <div className="flex items-center justify-center sm:justify-start gap-2 text-muted-foreground">
                  <RoleIcon className="w-4 h-4" />
                  <span className="capitalize">{profile?.role}</span>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAvatarClick}
                  disabled={uploadingAvatar}
                  data-testid="upload-avatar-btn"
                >
                  {uploadingAvatar ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4 mr-2" />
                  )}
                  {profile?.picture ? 'Change Photo' : 'Upload Photo'}
                </Button>
                
                {profile?.picture && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDeleteAvatar}
                    disabled={uploadingAvatar}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="delete-avatar-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove
                  </Button>
                )}
              </div>
              
              <p className="text-xs text-muted-foreground">
                Supported formats: JPG, PNG, GIF, WEBP. Max size: 5MB
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Profile Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-[#C85A42]" />
            Personal Information
          </CardTitle>
          <CardDescription>Update your personal details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="flex items-center gap-2">
              <User className="w-4 h-4 text-muted-foreground" />
              Full Name *
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter your full name"
              data-testid="profile-name-input"
            />
          </div>

          {/* Email (read-only) */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-muted-foreground" />
              Email Address
            </Label>
            <div className="flex items-center gap-2">
              <Input
                value={profile?.email || ''}
                disabled
                className="bg-muted"
              />
              <Badge variant="secondary" className="flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Verified
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Email address cannot be changed
            </p>
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-muted-foreground" />
              Phone Number
            </Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="e.g., +63 912 345 6789"
              data-testid="profile-phone-input"
            />
          </div>

          <Separator />

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#C85A42] hover:bg-[#B04A35]"
              data-testid="save-profile-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tenant Extended Profile Card - Only for tenants */}
      {profile?.role === 'tenant' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-[#C85A42]" />
              Extended Profile
            </CardTitle>
            <CardDescription>Additional information for your landlord's records</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Birthday and Age */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="birthday" className="flex items-center gap-2">
                  <Cake className="w-4 h-4 text-muted-foreground" />
                  Date of Birth
                </Label>
                <Input
                  id="birthday"
                  type="date"
                  value={formData.birthday}
                  onChange={(e) => setFormData({ ...formData, birthday: e.target.value })}
                  data-testid="profile-birthday-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  Age
                </Label>
                <div className="h-10 px-3 py-2 rounded-md border bg-muted flex items-center">
                  {formData.birthday ? (
                    <span className="font-medium">{calculateAge(formData.birthday)} years old</span>
                  ) : (
                    <span className="text-muted-foreground">Enter birthday to calculate</span>
                  )}
                </div>
              </div>
            </div>

            {/* Occupation */}
            <div className="space-y-2">
              <Label htmlFor="school_name" className="flex items-center gap-2">
                <GraduationCap className="w-4 h-4 text-muted-foreground" />
                School / University
              </Label>
              <Input
                id="school_name"
                value={formData.school_name}
                onChange={(e) => setFormData({ ...formData, school_name: e.target.value })}
                placeholder="e.g., UP Diliman, Ateneo de Manila"
                data-testid="profile-school-name-input"
              />
            </div>

            {/* Student ID */}
            <div className="space-y-2">
              <Label htmlFor="student_id" className="flex items-center gap-2">
                <GraduationCap className="w-4 h-4 text-muted-foreground" />
                Student ID Number
              </Label>
              <Input
                id="student_id"
                value={formData.student_id}
                onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
                placeholder="e.g., 2024-12345"
                data-testid="profile-student-id-input"
              />
            </div>

            {/* Home Address */}
            <div className="space-y-2">
              <Label htmlFor="home_address" className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                Complete Address
              </Label>
              <Textarea
                id="home_address"
                value={formData.home_address}
                onChange={(e) => setFormData({ ...formData, home_address: e.target.value })}
                placeholder="Enter your complete permanent home address (not your dormitory address)"
                rows={3}
                data-testid="profile-home-address-input"
              />
            </div>

            <Separator />

            {/* Emergency Contact */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <h3 className="font-semibold">Emergency Contact</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Someone we can contact in case of emergency
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="emergency_name">Contact Name *</Label>
                  <Input
                    id="emergency_name"
                    value={formData.emergency_contact.name}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      emergency_contact: { ...formData.emergency_contact, name: e.target.value }
                    })}
                    placeholder="e.g., Maria Santos"
                    data-testid="profile-emergency-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emergency_relationship">Relationship *</Label>
                  <Input
                    id="emergency_relationship"
                    value={formData.emergency_contact.relationship}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      emergency_contact: { ...formData.emergency_contact, relationship: e.target.value }
                    })}
                    placeholder="e.g., Mother, Father, Sibling"
                    data-testid="profile-emergency-relationship-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="emergency_phone">Contact Number *</Label>
                <Input
                  id="emergency_phone"
                  value={formData.emergency_contact.phone}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    emergency_contact: { ...formData.emergency_contact, phone: e.target.value }
                  })}
                  placeholder="e.g., +63 912 345 6789"
                  data-testid="profile-emergency-phone-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="emergency_address" className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  Contact Address
                </Label>
                <Textarea
                  id="emergency_address"
                  value={formData.emergency_contact.address}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    emergency_contact: { ...formData.emergency_contact, address: e.target.value }
                  })}
                  placeholder="Street, City, Province, ZIP"
                  rows={2}
                  data-testid="profile-emergency-address-input"
                />
              </div>
            </div>

            <Separator />

            {/* Save Button for Extended Profile */}
            <div className="flex justify-end">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#C85A42] hover:bg-[#B04A35]"
                data-testid="save-extended-profile-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Extended Profile
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Account Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#C85A42]" />
            Account Information
          </CardTitle>
          <CardDescription>Your account details and status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Account Type</p>
              <div className="flex items-center gap-2">
                <Badge className={getRoleColor(profile?.role)}>
                  <RoleIcon className="w-3 h-3 mr-1" />
                  {profile?.role?.charAt(0).toUpperCase() + profile?.role?.slice(1)}
                </Badge>
              </div>
            </div>
            
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Member Since</p>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">
                  {profile?.created_at ? formatDate(profile.created_at) : 'N/A'}
                </span>
              </div>
            </div>
            
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">User ID</p>
              <code className="text-xs bg-muted px-2 py-1 rounded">
                {profile?.user_id}
              </code>
            </div>
            
            {profile?.google_id && (
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Linked Accounts</p>
                <Badge variant="outline" className="flex items-center gap-1 w-fit">
                  <svg className="w-3 h-3" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google Connected
                </Badge>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Change Password Card */}
      {!profile?.google_id && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#C85A42]" />
              Change Password
            </CardTitle>
            <CardDescription>Update your account password for security</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Current Password */}
            <div className="space-y-2">
              <Label htmlFor="currentPassword" className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-muted-foreground" />
                Current Password *
              </Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showCurrentPassword ? "text" : "password"}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  placeholder="Enter your current password"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <Label htmlFor="newPassword" className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-muted-foreground" />
                New Password *
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  placeholder="Enter your new password (min 6 characters)"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Confirm New Password */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-muted-foreground" />
                Confirm New Password *
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  placeholder="Confirm your new password"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                <p className="text-xs text-red-500">Passwords do not match</p>
              )}
            </div>

            <Separator />

            {/* Change Password Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleChangePassword}
                disabled={changingPassword || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                className="bg-[#C85A42] hover:bg-[#B04A35]"
                data-testid="change-password-btn"
              >
                {changingPassword ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <KeyRound className="w-4 h-4 mr-2" />
                )}
                Change Password
              </Button>
            </div>

            <p className="text-xs text-muted-foreground">
              A confirmation email will be sent to your registered email address after the password change.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Google Account Notice */}
      {profile?.google_id && (
        <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900">
                <svg className="w-5 h-5 text-blue-600" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <div>
                <h4 className="font-medium text-blue-900 dark:text-blue-100">Google Account Connected</h4>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  Your account is linked to Google. Password changes are managed through your Google account settings.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete Account Section - Tenants Only */}
      {user?.role === 'tenant' && (
        <Card className="border-red-200 bg-red-50/50 dark:bg-red-950/20 dark:border-red-800">
          <CardHeader>
            <CardTitle className="text-lg text-red-700 dark:text-red-400 flex items-center gap-2">
              <UserX className="w-5 h-5" />
              Danger Zone
            </CardTitle>
            <CardDescription className="text-red-600/80 dark:text-red-400/80">
              Permanently delete your account and all associated data. This action cannot be undone.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-100 hover:text-red-700"
              onClick={() => setDeleteDialogOpen(true)}
              data-testid="delete-account-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete My Account
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Delete Your Account?
            </DialogTitle>
            <DialogDescription>
              This will permanently delete your account and all your data including:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <ul className="text-sm text-muted-foreground space-y-1 list-disc pl-5">
              <li>Your profile information</li>
              <li>All lease records</li>
              <li>All payment history</li>
              <li>Maintenance requests</li>
              <li>Form submissions</li>
            </ul>
            <p className="text-sm font-medium text-red-600">This action cannot be undone. Your landlord will be notified.</p>
            <div>
              <Label className="text-sm">Type <span className="font-bold">DELETE</span> to confirm</Label>
              <Input
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE"
                className="mt-1"
                data-testid="delete-confirm-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setDeleteConfirmText(''); }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={deleteConfirmText !== 'DELETE' || deletingAccount}
              data-testid="confirm-delete-account-btn"
            >
              {deletingAccount ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Permanently Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
